import { useEffect, useState, useCallback, useRef } from "react";
import { api } from "./api";

const POLL_MS = 60000; // 60s: keeps it fresh without hammering

const SEVERITY_STYLE = {
  critical: {
    label: "Critical",
    bar: "bg-red-600",
    chip: "bg-red-50 text-red-800 border-red-200",
    dot: "bg-red-500",
    banner: "border-red-300 bg-red-50",
    title: "text-red-900",
    body: "text-red-900/80",
  },
  high: {
    label: "High",
    bar: "bg-amber-500",
    chip: "bg-amber-50 text-amber-800 border-amber-200",
    dot: "bg-amber-500",
    banner: "border-amber-300 bg-amber-50",
    title: "text-amber-900",
    body: "text-amber-900/80",
  },
  medium: {
    label: "Medium",
    bar: "bg-sky-500",
    chip: "bg-sky-50 text-sky-800 border-sky-200",
    dot: "bg-sky-500",
    banner: "border-sky-300 bg-sky-50",
    title: "text-sky-900",
    body: "text-sky-900/80",
  },
  low: {
    label: "Low",
    bar: "bg-slate-400",
    chip: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
    banner: "border-slate-300 bg-slate-50",
    title: "text-slate-900",
    body: "text-slate-700",
  },
  info: {
    label: "Info",
    bar: "bg-slate-300",
    chip: "bg-slate-50 text-slate-600 border-slate-200",
    dot: "bg-slate-300",
    banner: "border-slate-200 bg-white",
    title: "text-slate-900",
    body: "text-slate-700",
  },
};

// `location_risk` warnings can carry the backend's internal "warning"
// severity string (see _LOC_SEVERITY_MAP in operational_warnings/router.py,
// which maps its "high" bucket to the literal string "warning" rather than
// "high"). Alias it to the same visual treatment as "high" so those warnings
// render with the correct color instead of silently falling back to "info".
SEVERITY_STYLE.warning = SEVERITY_STYLE.high;

export function severityStyle(sev) {
  return SEVERITY_STYLE[sev] || SEVERITY_STYLE.info;
}

// English fallback labels: mirrored under the `warnings.type.*` i18n keys
// in i18n/translations.js. Prefer passing `t` (from useT()) so the label
// follows the user's chosen language; the plain lookup below only kicks in
// for call sites that don't have a translator function on hand.
const TYPE_LABEL = {
  booking_overlap: "Overlapping bookings",
  buffer_violation: "Short turnaround",
  vehicle_unavailable: "Vehicle unavailable",
  incident_impact: "Vehicle issue",
  missing_fields: "Missing details",
  pending_too_long: "Awaiting confirmation",
  return_overdue: "Return overdue",
  deposit_unresolved: "Deposit unresolved",
  missing_return_inspection: "Inspection missing",
  inspection_damage: "Possible damage",
  vehicle_document_expiry: "Document expiring",
  location_risk: "Location / distance risk",
  maintenance_due: "Maintenance due",
};

export function typeLabel(type, t) {
  if (t) return t(`warnings.type.${type}`);
  return TYPE_LABEL[type] || type || "Warning";
}

// One static "why it matters" sentence per warning type: this never varies
// per warning instance (unlike title/message, which are backend-generated
// per occurrence), so it lives here as a small lookup instead of a DB field.
const WHY_IT_MATTERS = {
  booking_overlap: "Two customers are booked on the same vehicle at overlapping times: one of them won't have a car unless this is fixed before pickup.",
  buffer_violation: "There may not be enough time to clean, inspect, and prep the vehicle before the next customer arrives.",
  vehicle_unavailable: "The vehicle assigned to this booking isn't ready to rent: the customer may show up to no vehicle.",
  incident_impact: "An open incident on this vehicle could affect the customer's pickup or the vehicle's condition.",
  missing_fields: "Without full contact or scheduling details, staff may not be able to reach the customer or prep the booking correctly.",
  pending_too_long: "The customer hasn't confirmed after 24+ hours: the vehicle may be held for a booking that never happens.",
  return_overdue: "The vehicle hasn't been returned on time, which can delay or block the next booking on this vehicle.",
  deposit_unresolved: "The rental ended over a week ago and the deposit hasn't been refunded or withheld: this can turn into a dispute later.",
  missing_return_inspection: "Without a return inspection on file, damage or deposit disputes are hard to resolve later.",
  inspection_damage: "Unreviewed damage findings can hold up the deposit decision and any repair billing.",
  vehicle_document_expiry: "An expired or soon-to-expire document can make the vehicle non-compliant to rent out.",
  location_risk: "Pickup/return distance may affect this booking: confirm logistics with the customer.",
  maintenance_due: "Skipping or delaying scheduled service can lead to breakdowns, safety issues, or bigger repair bills later.",
};

export function whyItMatters(type, t) {
  if (t) return t(`warnings.why.${type}`);
  return WHY_IT_MATTERS[type] || "";
}

// `location_risk` warnings write `booking_id`/`item_id` (see
// _detect_location_risks in operational_warnings/router.py) instead of the
// `related_booking_id`/`related_vehicle_id` every other detector uses.
// These helpers paper over that mismatch on the frontend only, so cards can
// resolve the right booking/vehicle for location_risk warnings too, without
// touching the backend detector.
export function getBookingId(w) {
  return w?.related_booking_id || w?.booking_id || null;
}
export function getVehicleId(w) {
  return w?.related_vehicle_id || w?.item_id || null;
}

// Types eligible for the "Reassign vehicle" action (opens ReassignVehicleModal).
export const REASSIGN_ELIGIBLE_TYPES = [
  "booking_overlap", "buffer_violation", "vehicle_unavailable",
  "incident_impact", "return_overdue",
];

// Primary / secondary / extra action per warning type. `extra` is an
// additional link shown in the "More actions" menu alongside the constant
// Mark reviewed / Add note / Close items every type gets.
// Action kinds: "reassign" | "notify" | "booking" | "incident" | "vehicle_documents"
export const ACTION_PLAN = {
  booking_overlap:            { primary: "reassign", secondary: "notify" },
  buffer_violation:           { primary: "reassign", secondary: "notify" },
  vehicle_unavailable:        { primary: "reassign", secondary: "notify" },
  incident_impact:            { primary: "reassign", secondary: "notify", extra: "incident" },
  missing_fields:             { primary: "booking",  secondary: "notify" },
  pending_too_long:           { primary: "notify",   secondary: "booking" },
  return_overdue:             { primary: "notify",   secondary: "reassign" },
  deposit_unresolved:         { primary: "booking",  secondary: "notify" },
  missing_return_inspection:  { primary: "booking" },
  inspection_damage:          { primary: "incident" },
  vehicle_document_expiry:    { primary: "vehicle_documents" },
  location_risk:              { primary: "notify",   secondary: "booking" },
  maintenance_due:            { primary: "vehicle_maintenance" },
};

export function actionPlanFor(type) {
  return ACTION_PLAN[type] || {};
}

export function useWarningSummary() {
  const [summary, setSummary] = useState({ count: 0, by_severity: {}, top: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/warnings/summary");
      setSummary(data);
      setError(null);
    } catch (e) {
      // Quietly handle 401/403 (not a business user): we just show nothing.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        setSummary({ count: 0, by_severity: {}, top: null });
      } else {
        setError(e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, POLL_MS);
    return () => clearInterval(timer.current);
  }, [load]);

  return { summary, loading, error, reload: load };
}
